






import { GPageBase } from '@/mixins';
import { mixins } from 'vue-class-component';
import { Component, Prop } from 'vue-property-decorator';
import { PsychologicalSafety } from '@/models/school-group/school-group-trend-analysis';
import moment from 'moment';
import { store } from '@/store';

@Component({
  name: 'GTrendAnalysisPsychologicalSafetyChart',
})
export default class GTrendAnalysisPsychologicalSafetyChart extends mixins(GPageBase) {
  @Prop() data: PsychologicalSafety;
  chartOptions: any = {};

  mounted() {
    this.chartOptions = {
      title: { left: 'center', top: 10, text: this.$t('PSYCHOLOGICAL_SAFETY').toString() },
      legend: {
        data: [this.latestLabelName, this.lastLabelName, this.latestMedianName, this.lastMedianName],
        top: 35,
        center: 0,
      },
      tooltip: {
        axisPointer: {
          type: 'shadow',
        },
        formatter: (params: any) => {
          // 外れ値(未満)生徒名表示（最新）
          if (params.seriesName === this.latestLabelName && params.value[0] <= -90 && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipDataWithOutlierStudents(this.latestLabelName, params.value[0], params.value[params.encode.y[0]], '', this.displayLatestOutlierLowerStudentNames);
          }
          // 外れ値(超過)生徒名表示（最新）
          if (params.seriesName === this.latestLabelName && 90 <= params.value[0] && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipDataWithOutlierStudents(this.latestLabelName, params.value[0], params.value[params.encode.y[0]], '', this.displayLatestOutlierUpperStudentNames);
          }
          // 外れ値(未満)生徒名表示（前回）
          if (params.seriesName === this.lastLabelName && params.value[0] <= -90 && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipDataWithOutlierStudents(this.lastLabelName, params.value[0], params.value[params.encode.y[0]], '', this.displayLastOutlierLowerStudentNames);
          }
          // 外れ値(超過)生徒名表示（前回）
          if (params.seriesName === this.lastLabelName && 90 <= params.value[0] && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipDataWithOutlierStudents(this.lastLabelName, params.value[0], params.value[params.encode.y[0]], '', this.displayLastOutlierUpperStudentNames);
          }
          // 凡例表示（最新）
          if (params.seriesName === this.latestLabelName && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipData(this.latestLabelName, params.value[0], params.value[params.encode.y[0]], this.displayLatestNomalStudentNames(params.value[0]));
          }
          // 凡例表示（前回）
          if (params.seriesName === this.lastLabelName && params.value[params.encode.y[0]] > 0) {
            return this.createTooltipData(this.lastLabelName, params.value[0], params.value[params.encode.y[0]], this.displayLastNormalStudentNames(params.value[0]));
          }
          // 中央値表示（最新）
          if (params.seriesName === this.latestMedianName) {
            return `${this.latestMedianName}：${params.value[0]}`;
          }
          // 中央値表示（前回）
          if (params.seriesName === this.lastMedianName) {
            return `${this.lastMedianName}：${params.value[0]}`;
          }
          return;
        },
      },
      grid: { width: '80%', height: '80%', top: 'auto', left: 'auto' },
      xAxis: [
        {
          type: 'value',
          name: this.$t('COMFORT_LEVEL').toString(),
          nameLocation: 'center',
          nameTextStyle: { fontWeight: 'bold' },
          nameGap: 30,
          min: -100,
          max: 100,
        },
      ],
      yAxis: [
        {
          type: 'value',
          name: this.$t('NUMBER_OF_PEOPLE').toString(),
          nameLocation: 'center',
          nameTextStyle: { fontWeight: 'bold' },
          nameGap: 30,
          min: 0,
          max: this.yAxisMax,
          splitNumber: this.splitNumber,
        },
      ],
      series: [
        // 最新の中央値用データ
        {
          name: this.latestMedianName,
          type: 'custom',
          renderItem: this.data.latestData.examDate ? this.medianRender : null,
          itemStyle: { color: 'red' },
          data: [{ value: [this.data.latestMedian * 50, this.yAxisMax] }],
          z: 4,
        },
        // 前回の中央値用データ
        {
          name: this.lastMedianName,
          type: 'custom',
          renderItem: this.data.lastData.examDate ? this.medianRender : null,
          itemStyle: { color: 'orange' },
          data: [{ value: [this.data.lastMedian * 50, this.yAxisMax] }],
          z: 3,
        },
        {
          name: this.lastLabelName,
          type: 'bar',
          barGap: '0%',
          itemStyle: { opacity: 0.7, color: 'gray' },
          data: this.lastDistributionChartData,
          barWidth: 10,
          z: 2,
        },
        {
          name: this.latestLabelName,
          type: 'bar',
          barGap: '0%',
          itemStyle: { opacity: 0.7, color: 'blue' },
          data: this.latestDistributionChartData,
          barWidth: 10,
          z: 1,
        },
      ],
    };
  }

  addSeries() {}

  medianRender(params: any, api: any) {
    // 開始位置の計算
    const countMax = api.value(1);
    const start = api.coord([api.value(0), countMax]);
    //中央値のグラフの幅
    const MEDIAN_BAR_WIDTH = 3;

    return {
      type: 'rect',
      shape: {
        x: start[0] - MEDIAN_BAR_WIDTH,
        y: start[1],
        width: MEDIAN_BAR_WIDTH,
        height: api.getHeight() * 0.8,
      },
      style: api.style(),
    };
  }

  get yAxisMax(): number {
    return Math.ceil(this.countMax / this.splitNumber) * this.splitNumber;
  }

  get splitNumber(): number {
    // データの件数が5件未満の時にはその値に合わせた軸数にする
    return this.countMax < 5 ? this.countMax : 5;
  }

  get countMax(): number {
    const maxCountOfLatest = Math.max.apply(null, this.latestDistributionCountData);
    if (maxCountOfLatest === -Infinity) {
      //今回データがない場合、Math.max.applyは -Infinityを返す。そのときは暫定として1を返すことにする
      return 1;
    }
    const maxCountOfLast = Math.max.apply(null, this.lastDistributionCountData);
    if (maxCountOfLatest >= maxCountOfLast) {
      return maxCountOfLatest;
    } else {
      return maxCountOfLast;
    }
  }

  // ラベル名（今回）
  get latestLabelName() {
    if (this.data.latestData.examDate) {
      return this.$t('LATEST').toString() + `：（${moment(this.data.latestData.examDate).format('YYYY-MM-DD')}）`;
    }
    return this.$t('LATEST').toString() + `：（ー）`;
  }

  // ラベル名（前回）
  get lastLabelName() {
    if (this.data.lastData.examDate) {
      return this.$t('LAST_TIME').toString() + `：（${moment(this.data.lastData.examDate).format('YYYY-MM-DD')}）`;
    }
    return this.$t('LAST_TIME').toString() + `：（ー）`;
  }

  // ラベル名（中央値（今回））
  get latestMedianName() {
    return this.$t('MEDIAN').toString() + `（` + this.$t('LATEST').toString() + `）`;
  }

  // ラベル名（中央値（前回））
  get lastMedianName() {
    return this.$t('MEDIAN').toString() + `（` + this.$t('LAST_TIME').toString() + `）`;
  }

  // 今回の分布(Count)データ
  get latestDistributionCountData() {
    return this.data.latestData.trendData.map((d: any) => d.data[2]);
  }

  // 今回の分布(Chart)データ
  get latestDistributionChartData() {
    return this.data.latestData.trendData.map((d: any) => [d.data[0] + 5, d.data[2]]);
  }

  // 前回の分布(Count)データ
  get lastDistributionCountData() {
    return this.data.lastData.trendData.map((d: any) => d.data[2]);
  }

  // 前回の分布(Chart)データ
  get lastDistributionChartData() {
    return this.data.lastData.trendData.map((d: any) => [d.data[0] + 5, d.data[2]]);
  }

  // 生徒データ（最新）
  displayLatestNomalStudentNames(normalData: number) {
    var comparisonLatestStudentData = this.data.latestData.trendData.filter((d: any) => {
      //d.data[0]、d.data[1]で範囲の指定しているので、指定をすることで人数を絞りこむ
      if (d.data[0] <= normalData && normalData <= d.data[1]) {
        return d.userName;
      }
    });
    var latestFilterUserName = comparisonLatestStudentData.map((d: any) => d.userName);
    //そのままだとスペース、カンマが入るためツールチップの表示がおかしくなるため
    return latestFilterUserName
      .join(',')
      .split(',')
      .join('<br>');
  }

  // 生徒データ（前回）
  displayLastNormalStudentNames(normalData: number) {
    var comparisonLastStudentData = this.data.lastData.trendData.filter((d: any) => {
      if (d.data[0] <= normalData && normalData <= d.data[1]) {
        return d.userName;
      }
    });
    var lastFilterUserName = comparisonLastStudentData.map((d: any) => d.userName);
    return lastFilterUserName
      .join(',')
      .split(',')
      .join('<br>');
  }

  // 外れ値(未満)生徒データ（最新）
  get displayLatestOutlierLowerStudentNames(): string {
    return this.data.latestOutlierLowerStudents.map((student: any) => this.studentFullNameByLangCode(student)).join('<br>');
  }

  // 外れ値(超過)生徒データ（最新）
  get displayLatestOutlierUpperStudentNames(): string {
    return this.data.latestOutlierUpperStudents.map((student: any) => this.studentFullNameByLangCode(student)).join('<br>');
  }

  // 外れ値(未満)生徒データ（前回）
  get displayLastOutlierLowerStudentNames(): string {
    return this.data.lastOutlierLowerStudents.map((student: any) => this.studentFullNameByLangCode(student)).join('<br>');
  }

  // 外れ値(超過)生徒データ（前回）
  get displayLastOutlierUpperStudentNames(): string {
    return this.data.lastOutlierUpperStudents.map((student: any) => this.studentFullNameByLangCode(student)).join('<br>');
  }

  createTooltipData(labelName: string, xRangeLower: number, n: number, userName: any): string {
    // barChart表示用に+5しているので、区間を計算するために、調整を行っている
    return `${labelName}<br>` + this.$t('RANGE').toString() + `：${xRangeLower - 4} 〜 ${xRangeLower + 5}<br>` + this.$t('NUMBER_OF_PEOPLE').toString() + `：${n}<br>${userName}`;
  }

  createTooltipDataWithOutlierStudents(labelName: string, xRangeLower: number, n: number, userName: any, outlierUpperStudentNames: string): string {
    return this.createTooltipData(labelName, xRangeLower, n, userName) + `<br>` + this.$t('OUTLIER_STUDENT').toString() + `：<br>${outlierUpperStudentNames}`;
  }

  studentFullNameByLangCode(student: any): string {
    if (this.$store.getters.isNameOrder) {
      // 日本語と同じ 姓 + 名
     return `${student.lastName} ${student.firstName}`;
    } else {
      // 日本語とは異なる 名 + 姓
      return `${student.firstName} ${student.lastName}`;
    }
  }
}
